/**
 * URL <-> SearchState codec for shareable Search page links.
 *
 * Single source of truth in both directions: `searchStateToParams` writes it,
 * `paramsToSearchState` reads it back and validates every field (so a stale or
 * hand-edited link can't hydrate an impossible state). Multi-value filters use
 * repeated keys (?ns=A&ns=B); decode applies the UI's mode normalization
 * (status/sort dropped in translation mode; locales dropped in source mode).
 */
import { SearchFilters, TranslationStatus } from 'src/generated/graphql';
// Type-only import to avoid a runtime import cycle with SearchPage.
import type { SearchState, SearchMode } from './SearchPage';
import { NO_FILTERS } from './searchFilterUtils';
import {
  DEFAULT_SORT_STATE,
  isValidSortState,
  removeSourceOnlySortField,
  type SortState,
} from './sortState';
import type { UrlStateCodec } from 'src/common/urlSharing';

/**
 * Query-string keys. Kept short and human-readable so shared links stay
 * legible, and each concept uses the same token as its path segment (`ns`,
 * `sg` — see src/common/routes.ts), so one URL never spells a concept two ways.
 *
 * `sg` replaced `group` with no fallback read of the old key: the product is
 * pre-launch, so old-shape links are allowed to break (the same policy that
 * shipped the route prefixes without legacy redirects), and saved searches
 * persist the decoded state rather than URL strings, so no stored data carries
 * the old key. A dual-read would keep two spellings of one concept alive with
 * nothing to migrate.
 */
const PARAM = {
  QUERY: 'q',
  MODE: 'mode',
  EXACT: 'exact',
  NAMESPACE: 'ns',
  STATUS: 'status',
  STRING_GROUP: 'sg',
  CX_ID: 'cxId',
  CX_FAMILY: 'cxFamily',
  LOCALE: 'locale',
  SORT_FIELD: 'sort',
  SORT_ORDER: 'order',
} as const;

const MODE_TRANSLATION: SearchMode = 'TRANSLATION';
const MODE_SOURCE: SearchMode = 'SOURCE';

const STATUS_VALUES = new Set<string>(Object.values(TranslationStatus));

/** Narrows a raw string to a known `TranslationStatus`; unknown values dropped. */
function isTranslationStatus(value: string): value is TranslationStatus {
  return STATUS_VALUES.has(value);
}

/** Appends every value under one repeated key, no-op for empty/absent lists. */
function appendAll(
  params: URLSearchParams,
  key: string,
  values?: readonly string[] | null,
): void {
  values?.forEach((value) => params.append(key, value));
}

/** Encodes a SearchState into a shareable query string (non-default fields only). */
export function searchStateToParams(state: SearchState): URLSearchParams {
  const params = new URLSearchParams();

  if (state.queryText.trim().length > 0)
    params.set(PARAM.QUERY, state.queryText);
  if (state.mode === MODE_TRANSLATION) params.set(PARAM.MODE, MODE_TRANSLATION);
  if (state.exactMatch) params.set(PARAM.EXACT, '1');

  appendAll(params, PARAM.NAMESPACE, state.filters.namespaceIds);
  appendAll(params, PARAM.STRING_GROUP, state.filters.stringGroupIds);
  appendAll(params, PARAM.CX_ID, state.filters.cxIds);
  appendAll(params, PARAM.CX_FAMILY, state.filters.cxFamilies);
  appendAll(params, PARAM.STATUS, state.filters.statuses);
  appendAll(params, PARAM.LOCALE, state.filters.translatedLocales);

  if (state.sortState) {
    params.set(PARAM.SORT_FIELD, state.sortState.field);
    params.set(PARAM.SORT_ORDER, state.sortState.order);
  }

  return params;
}

/** Reads the sort pair back, returning the unsorted default for anything invalid. */
function parseSortState(params: URLSearchParams): SortState {
  const field = params.get(PARAM.SORT_FIELD);
  const order = params.get(PARAM.SORT_ORDER);
  const candidate = field != null && order != null ? { field, order } : null;
  return isValidSortState(candidate) ? candidate : DEFAULT_SORT_STATE;
}

/** Decodes + validates a query string into a SearchState, normalized to its mode. */
export function paramsToSearchState(params: URLSearchParams): SearchState {
  const mode: SearchMode =
    params.get(PARAM.MODE) === MODE_TRANSLATION
      ? MODE_TRANSLATION
      : MODE_SOURCE;
  const queryText = params.get(PARAM.QUERY) ?? '';
  const exactMatch = params.get(PARAM.EXACT) === '1';

  const filters: SearchFilters = { ...NO_FILTERS };
  const namespaceIds = params.getAll(PARAM.NAMESPACE);
  if (namespaceIds.length) filters.namespaceIds = namespaceIds;
  const stringGroupIds = params.getAll(PARAM.STRING_GROUP);
  if (stringGroupIds.length) filters.stringGroupIds = stringGroupIds;
  const cxIds = params.getAll(PARAM.CX_ID);
  if (cxIds.length) filters.cxIds = cxIds;
  const cxFamilies = params.getAll(PARAM.CX_FAMILY);
  if (cxFamilies.length) filters.cxFamilies = cxFamilies;

  if (mode === MODE_SOURCE) {
    const statuses = params.getAll(PARAM.STATUS).filter(isTranslationStatus);
    if (statuses.length) filters.statuses = statuses;
  } else {
    const locales = params.getAll(PARAM.LOCALE);
    if (locales.length) filters.translatedLocales = locales;
  }

  const sortState =
    mode === MODE_TRANSLATION
      ? removeSourceOnlySortField(parseSortState(params))
      : parseSortState(params);

  return { queryText, mode, exactMatch, filters, sortState };
}

/**
 * The Search page's shareable-URL codec, for {@link useUrlSharedState}. A
 * module-level constant so the hook's write callbacks keep a stable identity.
 */
export const SEARCH_URL_CODEC: UrlStateCodec<SearchState> = {
  encode: searchStateToParams,
  decode: paramsToSearchState,
};
